import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { AssetGroupingService, MeasurementUnitsService } from 'app/services';
import { Injectable } from '@angular/core';
import { AssetResponse, AssetUpdateRequest, AssetCreateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { EntityUtils, GridColumnHelper, EnumToComboValues, ClientSelectField, CheckBoxField } from '../utils';
import { AssetDetailsSectionHandler } from './details.section';
import { AssetCategoriesSectionHandler } from './categories.section';
import { AssetProfilesSectionHandler } from './profiles.section';
import { AssetSharingSectionHandler } from './sharing.section';
import { AssetEventsSectionHandler } from './events.section';
import { AssetAnalogEventsSectionHandler } from './analogs.section';
import { AssetZonesSectionHandler } from './zones.section';
import { ContactsSectionHandler } from '../shared/contacts.section';
import { AssetOdometerSectionHandler } from './odometer.section';
import { CustomFieldsSectionHandler } from '../shared/info.section';
import { ICONS, ASSET_ICONS } from '../icons';
import { RegisterEntityHandler } from '../../entities';
import { AssetCurfewEventsSectionHandler } from './curfews.section';
import { AssetRemindersSectionHandler } from './reminders.section';
import { AssetLocationSectionHandler } from './location.section';
import { HttpClient } from '@angular/common/http';
import { AssetLinkingSectionHandler } from './linking.section';
import { AppService } from 'app/app.service';
import { BACKEND_FEATURE_FLAGS } from '../client/backend.features';

@RegisterEntityHandler('asset')
@Injectable()
export class AssetHandler extends EntityHandler<AssetResponse> {

    constructor(
        private app: AppService,
        admin: AdminEditorService,
        private grouping: AssetGroupingService,
        private units: MeasurementUnitsService,
        private httpClient: HttpClient
    ) {
        super(admin);
    }

    states = ['inactive', 'active', 'suspended', 'deleted'];

    bulkActions = [
        this.admin.user.owner.type !== 'client' ?  { icon: 'share-square', name: this.admin.translate('EDITORS.ASSET.MOVE.TIP'), click: (assets) => this.moveAsset(assets) } : null,
    ].filter(x => x);

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {

        const filters = (filter || '').split(',');
        filters.push('owner.id=' + ownerId);
        return this.admin.getCached(`listAssets/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.entities.listAssets(ownerId, offset, limit, sort, filters.join(',')), 3).then(response => ({
            ...response,
            type: 'asset',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.ASSETS'),
        }));
    }

    getEntity(id: string): Promise<AssetResponse> {
        return this.admin.getCached(`getAsset/${id}`, api => api.entities.getAsset(id), 10);
    }

    createEntity(ownerId: string, entity: AssetCreateRequest): Promise<AssetResponse> {
        entity.ownerId = ownerId;
        return this.admin.applyUpdate(api => api.entities.createAsset(entity), { _type: 'asset' });
    }

    setEntityState(entity: AssetResponse, state: string): Promise<AssetResponse> {
        return this.admin.applyUpdate(api => api.entities.updateAsset(entity.id, { state: state as any }));
    }

    getGridColumns(ownerId: string): Promise<IKuiTableCol[]> {

        return this.admin.getCached(`getClient/${ownerId}`, api => api.accounts.getClient(ownerId), 60).then(client => {

            return Promise.all([
                this.grouping.getAssetCategories(client.id),
                this.grouping.getAssetGroups(client.id),
                this.grouping.getCostCentres(client.id),
                this.grouping.getAssetTypes(client.id),
            ]).then(([categories, assetGroups, costCentres, assetTypes]) => {

                const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.ASSET.COLUMNS');

                const columns: IKuiTableCol[] = [
                    grid.IconTextColumn('name', 'NAME', row => {
                        let icon = ICONS.asset;
                        const at = assetTypes.find(x => x.id === row.assetType?.id);
                        if (at) {
                            icon = ASSET_ICONS[at.data.tag] || icon;
                        }
                        return icon;
                    }),
                    grid.IdNameComboColumn('assetType', 'TYPE', EntityUtils.treeToComboFilter(assetTypes), 80),
                    grid.ComboColumn('state', 'STATE', EnumToComboValues(this.admin.i18n, 'ADMIN.STATES', this.states, ['deleted']), 80),
                    grid.IdNameComboColumn('costCentre', 'COST_CENTRE', EntityUtils.treeToComboFilter(costCentres), 150),
                    grid.IdNameArrayComboColumn('groups', 'GROUPS', EntityUtils.treeToComboFilter(assetGroups), 150, { value: 'OR', visible: true }),
                ];

                categories.filter(x => x.parent === 'root').forEach(category => {
                    const column = {
                        ...grid.IdNameComboColumn(`category.${category.index}`, category.name, EntityUtils.treeToComboFilter(categories.filter(x => x.parent !== 'root' && x.index === category.index)), 150),
                        format: (col, row) => {
                            const idx = parseFloat(col.field.replace('category.', ''));
                            return row.categories && row.categories[idx] && row.categories[idx].name;
                        },
                    };
                    column.filter.id = 'categories.id';
                    columns.push(column);
                });

                columns.push(grid.IdNameArrayTextColumn('devices', 'DEVICES'));
                columns.push(grid.IdNameTextColumn('assetTag', 'ASSET_TAG'));
                columns.push(grid.TextColumn('tags', 'TAGS'));
                // Cannot filter on ID column without API side changes, disable for now.
                columns.push({...grid.TextColumn('id', 'ID'), filter: null});

                [...(client.customFields['asset'] || []),
                    // ...(this.client.customFields[asset.assetType.id] || []), // TODO: add these in when we can sub select assets
                ].forEach(customField => {
                    columns.push({
                        ...grid.TextColumn(`fields.${customField.id}`, customField.title),
                        format: (_col, row) => {
                            return row.fields && row.fields[customField.id];
                        },
                    });
                });

                columns.push(grid.IdNameArrayTextColumn('sharedWith', 'SHARED_WITH'));
                columns.push(grid.TimeAgoColumn('modifiedDate', 'MODIFIED'));

                return columns;
            });
        });

    }

    async getEditorSections(ownerId: string, entity: AssetResponse): Promise<SectionHandlerGroup<AssetResponse>[]> {
        const owner = await this.app.api.accounts.getClient(ownerId);
        const backendFlags = (owner.meta?.mergedFlags && owner.meta.mergedFlags[BACKEND_FEATURE_FLAGS]) || {};

        const showAssetLinking = (): boolean => {
            // never show it to client users
            if (this.admin.user.owner.type === 'client') {
                return false;
            }
            // if the asset has been linked, always show the linking information
            if (entity?.linkedFrom || entity?.linkedTo?.length > 0) {
                return true;
            }

            return !!backendFlags['parsing-linking-enabled'];
        };


        return Promise.resolve([{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new AssetDetailsSectionHandler(this.admin, ownerId, this.grouping, this.httpClient),
                new AssetCategoriesSectionHandler(this.admin, ownerId, this.grouping),
                entity && entity.assetType && entity.assetType.name === 'Fixed Asset' && new AssetLocationSectionHandler(this.admin, ownerId),
                new CustomFieldsSectionHandler<AssetResponse>(this.admin, ownerId, 'asset'),
                new ContactsSectionHandler(this.admin, ownerId, 'asset'),
                showAssetLinking() && new AssetLinkingSectionHandler(this.app, this.admin, ownerId, this.units, this.httpClient),
            ].filter(x => x),
        }, {
            name: this.admin.i18n.instant('ADMIN.EDITOR.SETTINGS'),
            sections: [
                new AssetProfilesSectionHandler(this.admin, ownerId),
                new AssetSharingSectionHandler(this.admin, ownerId),
                new AssetOdometerSectionHandler(this.admin, ownerId, 'odometer'),
                new AssetOdometerSectionHandler(this.admin, ownerId, 'hours'),
                new AssetRemindersSectionHandler(this.admin, ownerId),
            ],
        }, {
            name: this.admin.i18n.instant('ADMIN.EDITOR.EVENTS'),
            sections: [
                new AssetEventsSectionHandler(this.admin, ownerId),
                new AssetCurfewEventsSectionHandler(this.admin, ownerId),
                new AssetAnalogEventsSectionHandler(this.admin, ownerId, this.units),
                new AssetZonesSectionHandler(this.admin, ownerId),
            ],
        }]);
    }

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: AssetUpdateRequest, sections: FormSectionHandler<AssetResponse, any>[] }> {
        return Promise.resolve({
            entityType: 'asset',
            entity: {}, // set up some defaults if you want
            sections: [
                new AssetDetailsSectionHandler(this.admin, ownerId, this.grouping, this.httpClient),
                new AssetCategoriesSectionHandler(this.admin, ownerId, this.grouping),
                new CustomFieldsSectionHandler<AssetResponse>(this.admin, ownerId, 'asset'),
            ],
        });
    }

    async moveAsset(assets: AssetResponse[]) {

        try {

            const result = await this.admin.showFormModal({
                groups: [{
                    name: this.admin.translate('EDITORS.ASSET.MOVE.MOVE_ASSET'),
                    description: this.admin.translate('EDITORS.ASSET.MOVE.MOVE_ASSET_DESC'),
                    fields: [
                        ClientSelectField('destinationClientId', this.admin.translate('EDITORS.ASSET.MOVE.DESTINATION_CLIENT'), true, this.admin.user.owner.id),
                        CheckBoxField('includeDevice', this.admin.translate('EDITORS.ASSET.MOVE.INCLUDE_DEVICE'), true),
                        CheckBoxField('includeSimCard', this.admin.translate('EDITORS.ASSET.MOVE.INCLUDE_SIMCARD'), true),
                    ],
                }],
            }, { destinationClientId: undefined, includeDevice: true, includeSimCard: true });

            if (result) {
                result.destinationClientId = result.destinationClientId.id;
                return this.admin.applyBulkUpdates((entity, payload) => {
                    return this.admin.applyUpdate(api => api.entities.moveAsset(entity.id, payload));
                }, assets, result);
            }
            
        } catch (err) {
            await this.admin.modal.error(this.admin.i18n.instant('ERROR.UNEXPECTED_ERROR', err));
        }

    }

}













